'use client'

import { captureMessage } from '@sentry/nextjs'
import { getMessaging, onMessage, MessagePayload } from 'firebase/messaging'
import Link from 'next/link'
import { useCallback, useEffect } from 'react'

import useFcmToken from '../../libs/hooks/use-fcm-token'

import useGetNotificationSettingsQuery from '@/libs/apis/queries/use-get-notification-settings'
import firebaseApp from '@/libs/configs/firebase'
import { useAppDispatch } from '@/libs/hooks/use-app-dispatch'
import { useAppSelector } from '@/libs/hooks/use-app-selector'
import useToastNotification from '@/libs/hooks/use-toast-notification'
import { setFcmToken } from '@/libs/store/slice/ui-slice'

export default function Fcm() {
  const { notificationPermissionStatus, requestNotificationPermission } = useFcmToken()
  const { fcmToken } = useAppSelector((state) => state.ui)
  const dispatch = useAppDispatch()
  const { show } = useToastNotification()
  const { data: notificationSettings } = useGetNotificationSettingsQuery()

  const notificationBody = (body: string, url: string) => {
    return (
      <div>
        <p className='text-sm font-normal text-pop-grey-900'>{body}</p>
        <Link href={url} className='text-sm font-medium text-pop-blue-400'>
          See Notification
        </Link>
      </div>
    )
  }

  const handleOnMessage = () => {
    try {
      if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
        if (notificationPermissionStatus === 'granted') {
          const messaging = getMessaging(firebaseApp)
          const unsubscribe = onMessage(messaging, (payload: MessagePayload) => {
            if (
              payload.data &&
              typeof payload.data.title === 'string' &&
              typeof payload.data.body === 'string' &&
              typeof payload.data.redirect === 'string'
            ) {
              const {
                data: { title, body, redirect },
              } = payload

              show({
                disableIcon: false,
                type: 'info',
                title,
                content: notificationBody(body, redirect),
              })
            }
          })

          return () => {
            unsubscribe() // Unsubscribe from the onMessage event on cleanup
          }
        }
      }
    } catch (error: any) {
      if (error?.name === 'NotFoundError') {
        // eslint-disable-next-line no-console
        console.error('IndexedDB object store not found:', error)
        captureMessage(`IndexedDB object store not found: ${error}`, 'info')
      } else {
        throw error
      }
    }
  }

  const handleGetFcmToken: any = useCallback(async () => {
    if (
      notificationPermissionStatus === 'granted' &&
      !fcmToken &&
      notificationSettings?.result?.webPushNotif
    ) {
      requestNotificationPermission()
    }

    if (notificationPermissionStatus === 'default' || notificationPermissionStatus === 'denied') {
      dispatch(setFcmToken(''))
    }
  }, [
    notificationSettings?.result?.webPushNotif,
    dispatch,
    notificationPermissionStatus,
    fcmToken,
    requestNotificationPermission,
  ])

  useEffect(() => {
    handleOnMessage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationPermissionStatus])

  useEffect(() => {
    handleGetFcmToken()
  }, [handleGetFcmToken])

  return null // This component is primarily for handling foreground notifications
}
